import { useCallback, useEffect, useState } from 'react';
import { logoVariantUrl } from './logoVariants';
/**
 * An <img>'s src and onError for a theme logo: the pre-sized variant first, and on its error the
 * original exactly as it was built before variants existed.
 *
 * @param {string} originalUrl - The logo url.
 * @param {number} cssPx - The largest CSS height the logo is drawn at (see pickLogoRung).
 * @returns {Object} `src` for the <img>, and `onError` to hand it.
 */
export function useSizedLogo(originalUrl, cssPx) {
    const variant = logoVariantUrl(originalUrl, cssPx);
    const [failedVariant, setFailedVariant] = useState();
    const src = variant && variant !== failedVariant ? variant : originalUrl;
    const onError = useCallback(() => {
        if (variant && src === variant) {
            setFailedVariant(variant);
        }
    }, [variant, src]);
    return { src, onError };
}
/**
 * The url a CSS mask or background should paint for a theme logo: the variant once a probe has
 * loaded it, the original once the probe has failed, undefined while the probe runs. Never both.
 *
 * @param {string} originalUrl - The logo url.
 * @param {number} cssPx - The largest CSS height the logo is drawn at (see pickLogoRung).
 * @param {string} crossOrigin - As the painting request is made; 'anonymous' for a mask-image.
 * @returns {string|undefined}
 */
export function useProbedLogoUrl(originalUrl, cssPx, crossOrigin) {
    const variant = logoVariantUrl(originalUrl, cssPx);
    // Keyed by the ORIGINAL: two originals of one stem (logo-full.png, then .svg) share a variant
    // but not a fallback.
    const [probed, setProbed] = useState({});
    useEffect(() => {
        if (!originalUrl || !variant) {
            return;
        }
        let live = true;
        probeImage(variant, crossOrigin).then(ok => {
            if (live) {
                setProbed({ for: originalUrl, url: ok ? variant : originalUrl });
            }
        });
        return () => {
            live = false;
        };
    }, [originalUrl, variant, crossOrigin]);
    if (!variant) {
        return originalUrl;
    }
    return probed.for === originalUrl ? probed.url : undefined;
}
/**
 * Whether an image loads. For a CSS background or mask, which has no error event: probe the variant
 * and paint it on success, the original on failure — never both layered, which would download the
 * original anyway.
 *
 * @param {string} url - The image to probe.
 * @param {string} crossOrigin - Set it as the painting request will be made: a mask-image is a CORS
 * request ('anonymous'), a background-image is not (undefined), so the probe's response is the one
 * the paint reuses from cache.
 * @returns {Promise<boolean>}
 */
export function probeImage(url, crossOrigin) {
    return new Promise(resolve => {
        const img = new Image();
        if (crossOrigin !== undefined) {
            img.crossOrigin = crossOrigin;
        }
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
    });
}
